/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 *
 * Both the bundle that defines `Shopify.actions` and the module that
 * registers <theme-drawer> land asynchronously, so this file waits for
 * each instead of assuming it is already there. Guessing wrong used to
 * send the shopper to /cart on a slow connection, with no error.
 */

const DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
const WAIT_TIMEOUT_MS = 3000;

/**
 * Resolves once `predicate` returns a truthy value, or after the timeout.
 * @param {() => unknown} predicate
 * @returns {Promise<void>}
 */
function waitFor(predicate) {
  if (predicate()) return Promise.resolve();

  return new Promise((resolve) => {
    const deadline = Date.now() + WAIT_TIMEOUT_MS;

    const tick = () => {
      if (predicate() || Date.now() >= deadline) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
}

function goToCart() {
  window.location.href = Theme.routes.cart_url || '/cart';
}

/**
 * The element exists in the DOM from first paint, but it is a plain
 * HTMLElement until theme-drawer.js runs. Only after the upgrade does
 * `open` become a method.
 * @returns {Promise<(HTMLElement & { open: () => void }) | null>}
 */
async function getUpgradedDrawer() {
  const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
    document.querySelector(DRAWER_SELECTOR)
  );

  // No drawer in the DOM: cart_type is 'page', or we are on the cart template.
  if (!drawer) return null;

  if (typeof drawer.open !== 'function') {
    await Promise.race([
      customElements.whenDefined('theme-drawer'),
      new Promise((resolve) => setTimeout(resolve, WAIT_TIMEOUT_MS)),
    ]);
    customElements.upgrade(drawer);
  }

  return typeof drawer.open === 'function'
    ? /** @type {HTMLElement & { open: () => void }} */ (drawer)
    : null;
}

function configure() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector(DRAWER_SELECTOR) ?? document,
  });

  actions.openCart.configure({
    async handler() {
      const drawer = await getUpgradedDrawer();

      if (drawer) {
        drawer.open();
      } else {
        goToCart();
      }
    },
  });
}

async function init() {
  // `Shopify.actions` is attached by a separate bundle that may land after
  // DOMContentLoaded. Bailing out silently here left the default handler in
  // place, which is how the drawer stopped opening at all.
  await waitFor(() => window.Shopify?.actions);
  configure();
}

init();
